import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { FALLBACK_INDUSTRY, PATHS, runtimeConfig } from '@/constants';
import { AgentIntroSourceType } from '@/pages/AgentIntro/components/TrainingSourceSelector/TrainingSourceSelector.type.ts';
import { buildNavigatePath } from '@/routes.tsx';
import { agentRequestRefreshService } from '@/services/AgentRequestRefreshService';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import { useAgentTraining } from '@/store/agent-training.ts';
import useAppStore from '@/store/app';
import { AgentInfoState } from '@/types/agent-realtime';
import { AgentCreationStep } from '@/types/agent-training.ts';

export const useRestoreProcess = () => {
	const { setAgentStep } = useAgentTraining();
	const navigate = useNavigate();
	const {
		clientSecret,
		connectorIdn,
		externalId,
		setAgentData,
		setCustomerData
	} = useAppStore(
		useShallow((state) => ({
			agentData: state.agentData,
			customerData: state.customerData,
			clientSecret: state.clientSecret,
			connectorIdn: state.connectorIdn,
			externalId: state.externalId,
			setAgentData: state.setAgentData,
			setCustomerData: state.setCustomerData
		}))
	);

	const sendInitialAgentRequest = useCallback(() => {
		agentRequestRefreshService.sendInitialAgentRequest({
			clientSecret,
			connectorIdn,
			externalId
		});
	}, [clientSecret, connectorIdn, externalId]);

	const sendProtectedReconnectAgentRequest = useCallback(() => {
		agentRequestRefreshService.sendProtectedReconnectAgentRequest({
			clientSecret,
			connectorIdn,
			externalId
		});
	}, [clientSecret, connectorIdn, externalId]);

	useEffect(() => {
		const offConnectCommand = agentTrainingRealtime.onConnect(
			sendInitialAgentRequest
		);

		const offReconnectCommand = agentTrainingRealtime.onReconnect(
			sendProtectedReconnectAgentRequest
		);

		return () => {
			offConnectCommand();
			offReconnectCommand();
			agentRequestRefreshService.cleanup();
		};
	}, [sendInitialAgentRequest, sendProtectedReconnectAgentRequest]);

	useEffect(() => {
		const offCommand = agentTrainingRealtime.onAgentInfo(({ body, state }) => {
			setAgentData({
				...body,
				scraper_type:
					body.scraper_type === AgentIntroSourceType.Website
						? AgentIntroSourceType.Website
						: AgentIntroSourceType.GoogleMap
			});

			setCustomerData({
				contact_email: body.contact_email,
				contact_phone: body.contact_phone,
				contact_fullname: body.contact_fullname,
				industry: body.industry || FALLBACK_INDUSTRY
			});

			if (state === AgentInfoState.Created) {
				if (runtimeConfig.isRemoteBuild) {
					setAgentStep(AgentCreationStep.SUCCESS);
					return;
				}

				navigate(buildNavigatePath(PATHS.agentSuccess));
			}
		});

		return () => {
			offCommand();
		};
	}, [setAgentData, setCustomerData, setAgentStep, navigate]);
};
